/**
 * The formula editor's helpers: colours, completion and signature help.
 *
 * NOTHING HERE EVALUATES. What a formula is worth, whether it reads and what
 * it means are the engine's answers (`POST /v1/dashboard/preview`); this only
 * cuts the text into pieces to colour, and finds the names a half-typed word
 * could become.
 */
import type { Drawn, Fix, Preset, Vocabulary, WidgetDraft } from '@/features/mitra/api/dashboard'

/** A repair applied to a draft: its formula, and the kind or unit it sets. */
export function applyFix(draft: WidgetDraft, fix: Fix): WidgetDraft {
  return {
    ...draft,
    ...(fix.formula !== undefined ? { formula: fix.formula } : {}),
    ...(fix.kind ? { kind: fix.kind } : {}),
    ...(fix.unit ? { unit: fix.unit } : {}),
  }
}

/** A drawn widget back to the definition the reader edits. */
export function draftOf(widget: Drawn): WidgetDraft {
  return {
    ...(widget.id ? { id: widget.id } : {}),
    title: widget.title,
    formula: widget.formula,
    kind: widget.kind,
    unit: widget.unit,
    decimals: widget.decimals,
    section: widget.section,
    scope: widget.scope,
    // Carried as the engine drew them, so an edit keeps the window it had.
    ...(widget.time ? { time: widget.time } : {}),
    ...(widget.rank ? { rank: widget.rank } : {}),
    ...(widget.limit ? { limit: widget.limit } : {}),
    ...(widget.where ? { where: widget.where } : {}),
  }
}

/** A preset as the engine's preview takes it, keyed by the preset's own key. */
export function presetDraft(preset: Preset): WidgetDraft & { id: string } {
  return {
    id: preset.key,
    title: preset.title,
    formula: preset.formula,
    kind: preset.kind,
    unit: preset.unit,
    decimals: preset.decimals,
    ...(preset.scope ? { scope: preset.scope } : {}),
    ...(preset.time ? { time: preset.time } : {}),
    ...(preset.rank ? { rank: preset.rank } : {}),
    ...(preset.limit ? { limit: preset.limit } : {}),
  }
}

/** A new measure before the reader has written anything. */
export const BLANK: WidgetDraft = {
  title: '',
  formula: '',
  kind: 'number',
  unit: 'NPR',
  decimals: 2,
}

/** How long the formula box waits after the last keystroke before asking. */
export const CHECK_DELAY_MS = 400

/** The ready-made measures an empty dashboard offers to start with. */
export const STARTER_KEYS = ['book_value', 'total_gain_pct', 'sector_weight'] as const

export type TokenKind =
  'space' | 'number' | 'text' | 'name' | 'op' | 'open' | 'close' | 'comma' | 'other'

export interface Token {
  kind: TokenKind
  start: number
  end: number
  text: string
}

const NAME_CHAR = /[\p{L}\p{M}\p{N}_]/u
const NAME_START = /[\p{L}_]/u
const OPS = '+-*/<>=!'

const at = (text: string, i: number): string => text.charAt(i)

/**
 * A formula cut into pieces for colouring. Lenient where the engine is
 * strict: an unclosed quote runs to the end and anything unknown is
 * `other`, so a half-typed formula still colours as far as it goes.
 */
export function formulaTokens(text: string): Token[] {
  const out: Token[] = []
  let i = 0
  while (i < text.length) {
    const ch = at(text, i)
    const start = i
    let kind: TokenKind
    if (/\s/.test(ch)) {
      while (i < text.length && /\s/.test(at(text, i))) i++
      kind = 'space'
    } else if (/[\p{Nd}.]/u.test(ch) && (ch !== '.' || /\p{Nd}/u.test(at(text, i + 1)))) {
      while (i < text.length && /[\p{Nd}.]/u.test(at(text, i))) i++
      kind = 'number'
    } else if (ch === "'" || ch === '"' || ch === '‘' || ch === '“') {
      const closing = ch === '‘' ? '’' : ch === '“' ? '”' : ch
      i++
      while (i < text.length && at(text, i) !== closing) i++
      i = Math.min(i + 1, text.length)
      kind = 'text'
    } else if (NAME_START.test(ch)) {
      while (i < text.length && NAME_CHAR.test(at(text, i))) i++
      kind = 'name'
    } else if (ch === '(') {
      i++
      kind = 'open'
    } else if (ch === ')') {
      i++
      kind = 'close'
    } else if (ch === ',') {
      i++
      kind = 'comma'
    } else if (OPS.includes(ch)) {
      while (i < text.length && OPS.includes(at(text, i)) && i - start < 2) i++
      kind = 'op'
    } else {
      i++
      kind = 'other'
    }
    out.push({ kind, start, end: i, text: text.slice(start, i) })
  }
  return out
}

/** A function's name as the help sheet writes its signature: `sum(x, …)` → `sum`. */
export function functionName(signature: string): string {
  return (signature.split('(')[0] ?? '').trim()
}

export interface Suggestion {
  /** What is inserted. */
  insert: string
  /** What the row shows first: the name, or "kitta → quantity". */
  label: string
  detail: string
  unit: string | null
  sample: string | null
  isFunction: boolean
}

/** The partial name the caret is at the end of, outside any quote. */
export function wordAt(text: string, caret: number): { start: number; word: string } | null {
  let start = caret
  while (start > 0 && NAME_CHAR.test(at(text, start - 1))) start--
  if (start === caret || !NAME_START.test(at(text, start))) return null
  const quotes = (text.slice(0, start).match(/['"]/g) ?? []).length
  if (quotes % 2 === 1) return null
  return { start, word: text.slice(start, caret) }
}

const LIMIT = 6

/** Up to six names, functions and everyday words a partial name could be:
 *  starts-with first, then contains. */
export function completions(
  text: string,
  caret: number,
  vocabulary: Vocabulary,
): { start: number; end: number; items: Suggestion[] } | null {
  const found = wordAt(text, caret)
  if (!found) return null
  const typed = found.word.toLowerCase()
  const rank = (name: string) =>
    name.toLowerCase().startsWith(typed) ? 0 : name.toLowerCase().includes(typed) ? 1 : 2
  const items: (Suggestion & { order: number })[] = []
  for (const v of vocabulary.variables) {
    const order = rank(v.name)
    if (order < 2 && v.name.toLowerCase() !== typed) {
      const sample = vocabulary.samples[v.name]
      items.push({
        insert: v.name,
        label: v.name,
        detail: v.meaning,
        unit: v.unit === 'text' ? null : v.unit,
        sample: sample ? `${sample.symbol} ${sample.value}` : null,
        isFunction: false,
        order,
      })
    }
  }
  for (const f of vocabulary.functions) {
    const name = functionName(f.name)
    const order = rank(name)
    if (order < 2 && name !== typed) {
      items.push({
        insert: name,
        label: f.name,
        detail: f.meaning,
        unit: null,
        sample: null,
        isFunction: true,
        order: order + 0.5,
      })
    }
  }
  for (const [word, name] of Object.entries(vocabulary.aliases)) {
    if (word.toLowerCase().startsWith(typed) && !items.some((i) => i.insert === name)) {
      const meaning = vocabulary.variables.find((v) => v.name === name)?.meaning ?? ''
      items.push({
        insert: name,
        label: `${word} → ${name}`,
        detail: meaning,
        unit: null,
        sample: null,
        isFunction: false,
        order: 1.5,
      })
    }
  }
  items.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label))
  const top: Suggestion[] = items.slice(0, LIMIT).map((item) => ({
    insert: item.insert,
    label: item.label,
    detail: item.detail,
    unit: item.unit,
    sample: item.sample,
    isFunction: item.isFunction,
  }))
  return top.length ? { start: found.start, end: caret, items: top } : null
}

/** The function call the caret is inside, and which of its arguments. */
export function signatureAt(
  text: string,
  caret: number,
  vocabulary: Vocabulary,
): { signature: string; params: string[]; index: number; meaning: string } | null {
  // Forward, so quotes pair as the engine pairs them: the brackets still
  // open at the caret, each with the commas seen inside it.
  const open: { at: number; commas: number }[] = []
  let quote: string | null = null
  for (let i = 0; i < caret; i++) {
    const ch = at(text, i)
    if (quote) {
      if (ch === quote) quote = null
      continue
    }
    if (ch === "'" || ch === '"') quote = ch
    else if (ch === '(') open.push({ at: i, commas: 0 })
    else if (ch === ')') open.pop()
    else if (ch === ',') {
      const top = open[open.length - 1]
      if (top) top.commas++
    }
  }
  const inner = open[open.length - 1]
  if (!inner || quote) return null
  let end = inner.at
  while (end > 0 && /\s/.test(at(text, end - 1))) end--
  let start = end
  while (start > 0 && NAME_CHAR.test(at(text, start - 1))) start--
  const name = text.slice(start, end).toLowerCase()
  const fn = vocabulary.functions.find((f) => functionName(f.name) === name)
  if (!fn) return null
  const params = fn.name
    .slice(fn.name.indexOf('(') + 1, fn.name.lastIndexOf(')'))
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  return {
    signature: fn.name,
    params,
    index: Math.max(0, Math.min(inner.commas, params.length - 1)),
    meaning: fn.meaning,
  }
}
